import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import ComposeMessageModal from '../components/ComposeMessageModal';
import {
    Mail,
    LogOut,
    Heart,
    Home,
    ChevronRight,
    ChevronLeft,
    Search,
    Eye,
    X,
    Plus,
    Send,
    Clock,
    AlertCircle,
    User,
    Calendar,
    FileText,
    TrendingUp,
    Activity,
    Edit,
    Trash2,
    Zap,
    CheckCircle,
    XCircle
} from 'lucide-react';

const sanitizeMessageHtml = (content = '') => DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'style'],
    ADD_ATTR: ['target'],
    ADD_TAGS: [],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'svg', 'math']
});

const AdminMailing = () => {
    const { user, logout, isAuthenticated, isReadOnly } = useAuth();
    const navigate = useNavigate();

    // États principaux
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [attachmentFilter, setAttachmentFilter] = useState('all'); // Nouveau: filtre pièces jointes
    const [stats, setStats] = useState({});

    // États pour modal et actions
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showComposeModal, setShowComposeModal] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null); // Nouveau: message en cours d'édition
    const [loadingActions, setLoadingActions] = useState({});
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });

    // Pagination
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1
    });

    const showNotification = useCallback((message, type = 'info') => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 5000);
    }, []);

    const fetchMessages = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();

            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (typeFilter !== 'all') params.append('type', typeFilter);
            if (attachmentFilter !== 'all') params.append('has_attachments', attachmentFilter === 'with');
            params.append('per_page', '10'); // 10 éléments par page

            const data = await apiService.getMessages(Object.fromEntries(params));

            setMessages(data.data || []);
            if (data.current_page !== undefined) {
                setPagination({
                    current_page: data.current_page,
                    per_page: data.per_page,
                    total: data.total,
                    last_page: data.last_page
                });
            }
        } catch (error) {
            console.error('Erreur lors du chargement des messages:', error);
            showNotification('Erreur lors du chargement des messages', 'error');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, statusFilter, typeFilter, attachmentFilter, showNotification]);

    const fetchStats = useCallback(async () => {
        try {
            const data = await apiService.getMessagesStats();
            setStats(data);
        } catch (error) {
            console.error('Erreur lors du chargement des statistiques:', error);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchMessages();
        fetchStats();
    }, [isAuthenticated, navigate, fetchMessages, fetchStats]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Erreur lors de la déconnexion:', error);
        }
    };

    const handleSendMessage = async (messageId, sendNow = false) => {
        try {
            setLoadingActions(prev => ({ ...prev, [messageId]: 'sending' }));

            await apiService.sendMessage(messageId, sendNow);

            showNotification(
                sendNow ? 'Message envoyé avec succès' : 'Message programmé pour envoi',
                'success'
            );
            fetchMessages();
            fetchStats();
        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            showNotification('Erreur lors de l\'envoi du message', 'error');
        } finally {
            setLoadingActions(prev => {
                const newState = { ...prev };
                delete newState[messageId];
                return newState;
            });
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
            return;
        }

        try {
            setLoadingActions(prev => ({ ...prev, [messageId]: 'deleting' }));

            await apiService.deleteMessage(messageId);

            showNotification('Message supprimé avec succès', 'success');
            fetchMessages();
            fetchStats();
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            showNotification('Erreur lors de la suppression du message', 'error');
        } finally {
            setLoadingActions(prev => {
                const newState = { ...prev };
                delete newState[messageId];
                return newState;
            });
        }
    };

    const openMessageModal = (message) => {
        setSelectedMessage(message);
        setShowMessageModal(true);
    };

    const closeMessageModal = () => {
        setSelectedMessage(null);
        setShowMessageModal(false);
    };

    // Nouvelle fonction pour éditer un message
    const openEditModal = (message) => {
        setEditingMessage(message);
        setShowComposeModal(true);
    };

    const closeEditModal = () => {
        setEditingMessage(null);
        setShowComposeModal(false);
    };

    const changePage = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page && newPage !== pagination.current_page) {
            setPagination(prev => ({ ...prev, current_page: newPage }));
            // Déclencher le rechargement des messages avec la nouvelle page
            const params = new URLSearchParams();
            params.append('page', newPage);
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (typeFilter !== 'all') params.append('type', typeFilter);
            
            apiService.getMessages(Object.fromEntries(params)).then(data => {
                setMessages(data.data || []);
                setPagination({
                    current_page: data.current_page,
                    per_page: data.per_page,
                    total: data.total,
                    last_page: data.last_page
                });
            }).catch(error => {
                console.error('Erreur lors du changement de page:', error);
                showNotification('Erreur lors du changement de page', 'error');
            });
        }
    };

    const handleMessageCreated = (newMessage) => {
        if (editingMessage) {
            // Mise à jour d'un message existant
            showNotification('Message modifié avec succès', 'success');
        } else {
            // Création d'un nouveau message
            showNotification('Message créé avec succès', 'success');
        }
        fetchMessages();
        fetchStats();
        closeEditModal();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'draft': return <Edit className="w-4 h-4" />;
            case 'scheduled': return <Clock className="w-4 h-4" />;
            case 'sending': return <Zap className="w-4 h-4" />;
            case 'sent': return <CheckCircle className="w-4 h-4" />;
            case 'failed': return <XCircle className="w-4 h-4" />;
            default: return <FileText className="w-4 h-4" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
            case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'sending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'sent': return 'bg-green-100 text-green-800 border-green-200';
            case 'failed': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            draft: 'Brouillon',
            scheduled: 'Programmé',
            sending: 'En cours d\'envoi',
            sent: 'Envoyé',
            failed: 'Échec'
        };
        return labels[status] || status;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(dateString));
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-center mb-2">Connexion requise</h2>
                    <p className="text-gray-600 text-center">Veuillez vous connecter pour accéder à cette page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Notification */}
            {notification.show && (
                <div className={`fixed top-2 sm:top-4 right-2 sm:right-4 z-50 px-4 sm:px-6 py-3 sm:py-4 rounded-lg shadow-lg max-w-xs sm:max-w-sm ${
                    notification.type === 'success' ? 'bg-green-500 text-white' :
                        notification.type === 'error' ? 'bg-red-500 text-white' :
                            'bg-blue-500 text-white'
                    }`}>
                    <span className="text-sm sm:text-base break-words">{notification.message}</span>
                </div>
            )}

            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-3 sm:space-y-0">
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <div className="flex items-center text-sm text-gray-500">
                                <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                                <span className="font-medium text-gray-900 hidden sm:inline">Dashboard</span>
                                <span className="font-medium text-gray-900 sm:hidden">Admin</span>
                                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-purple-600 font-semibold truncate">Gestion Mailing</span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 sm:space-x-6 w-full sm:w-auto justify-between sm:justify-end">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-medium text-gray-900">
                                    {user?.first_name || user?.name || 'Admin'} {user?.last_name || ''}
                                </p>
                                <p className="text-xs text-gray-500 flex items-center justify-end">
                                    <Heart className="w-3 h-3 mr-1 text-red-500" />
                                    <span className="hidden lg:inline">Fleurs de la Sainte Famille</span>
                                    <span className="lg:hidden">FSF</span>
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center px-2 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                title="Retour au Dashboard"
                            >
                                <Home className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Retour au Dashboard</span>
                            </button>

                            <button
                                onClick={handleLogout}
                                className="flex items-center px-2 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                title="Déconnexion"
                            >
                                <LogOut className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Déconnexion</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* En-tête de la page */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                        <div className="flex-1">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                                <Mail className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-purple-600" />
                                <span className="truncate">Gestion des Messages</span>
                            </h1>
                            <p className="mt-2 text-gray-600 text-sm sm:text-base">
                                Créez et envoyez des messages à tous les membres de la communauté FSF
                            </p>
                        </div>

                        {!isReadOnly && (
                        <button
                            onClick={() => setShowComposeModal(true)}
                            className="bg-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center font-medium text-sm sm:text-base w-full sm:w-auto justify-center"
                        >
                            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                            <span className="sm:hidden">Nouveau</span>
                            <span className="hidden sm:inline">Nouveau Message</span>
                        </button>
                        )}
                    </div>
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200">
                        <div className="flex items-center">
                            <div className="bg-blue-100 p-2 sm:p-3 rounded-lg">
                                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Messages</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200">
                        <div className="flex items-center">
                            <div className="bg-green-100 p-2 sm:p-3 rounded-lg">
                                <Send className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Messages Envoyés</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.sent || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200">
                        <div className="flex items-center">
                            <div className="bg-yellow-100 p-2 sm:p-3 rounded-lg">
                                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Programmés</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.scheduled || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200">
                        <div className="flex items-center">
                            <div className="bg-purple-100 p-2 sm:p-3 rounded-lg">
                                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Taux d'ouverture</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{(stats.avg_open_rate || 0).toFixed(1)}%</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filtres et recherche */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                        {/* Recherche */}
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Recherche
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Rechercher par sujet ou contenu..."
                                    className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all text-sm sm:text-base"
                                />
                            </div>
                        </div>

                        {/* Filtre par statut */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Statut
                            </label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all text-sm sm:text-base"
                            >
                                <option value="all">Tous les statuts</option>
                                <option value="draft">Brouillons</option>
                                <option value="scheduled">Programmés</option>
                                <option value="sending">En cours d'envoi</option>
                                <option value="sent">Envoyés</option>
                                <option value="failed">Échecs</option>
                            </select>
                        </div>

                        {/* Filtre par type */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Type
                            </label>
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all text-sm sm:text-base"
                            >
                                <option value="all">Tous les types</option>
                                <option value="email">Email</option>
                                <option value="sms">SMS</option>
                                <option value="notification">Notification</option>
                            </select>
                        </div>

                        {/* Filtre par pièces jointes */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <span className="hidden sm:inline">Pièces jointes</span>
                                <span className="sm:hidden">Fichiers</span>
                            </label>
                            <select
                                value={attachmentFilter}
                                onChange={(e) => setAttachmentFilter(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all text-sm sm:text-base"
                            >
                                <option value="all">Tous les messages</option>
                                <option value="with">Avec pièces jointes</option>
                                <option value="without">Sans pièces jointes</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Liste des messages */}
                <div className="bg-white text-justify rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Messages ({pagination.total})</h2>
                        <button
                            onClick={() => window.location.reload()}
                            className="text-gray-500 hover:text-gray-700 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100"
                            title="Actualiser"
                        >
                            <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-8 sm:py-12">
                            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-purple-600"></div>
                            <span className="ml-3 text-gray-600 text-sm sm:text-base">Chargement des messages...</span>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-8 sm:py-12">
                            <Mail className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                            <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">Aucun message trouvé</h3>
                            <p className="mt-2 text-gray-500 text-sm sm:text-base px-4">
                                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                                    ? "Aucun message ne correspond à vos critères de recherche."
                                    : isReadOnly ? "Aucun message disponible." : "Commencez par créer votre premier message."}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Vue mobile - Cartes */}
                            <div className="sm:hidden space-y-4 p-4">
                                {messages.map((message) => (
                                    <div key={message.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        {/* En-tête de la carte */}
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1 min-w-0">
                                                <h3 
                                                    className="font-semibold text-gray-900 truncate text-sm cursor-pointer hover:text-purple-600 transition-colors" 
                                                    onClick={() => openMessageModal(message)}
                                                >
                                                    {message.subject}
                                                </h3>
                                                <p className="text-xs text-gray-600 mt-1">
                                                    {message.recipients_count} destinataires
                                                </p>
                                                {message.sender && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Par: {message.sender.first_name} {message.sender.last_name}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end space-y-2 ml-2">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                                                    {getStatusIcon(message.status)}
                                                    <span className="ml-1">{getStatusLabel(message.status)}</span>
                                                </span>
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    {message.type === 'email' && <Mail className="w-3 h-3 mr-1" />}
                                                    {message.type}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Informations de la carte */}
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-gray-500">Créé:</span>
                                                <span className="font-medium text-gray-700">
                                                    {formatDate(message.created_at)}
                                                </span>
                                            </div>
                                            {message.scheduled_at && (
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-gray-500">Programmé:</span>
                                                    <span className="font-medium text-gray-700">
                                                        {formatDate(message.scheduled_at)}
                                                    </span>
                                                </div>
                                            )}
                                            {message.status === 'sent' && (
                                                <>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-500">Livraison:</span>
                                                        <span className="font-medium text-green-600">
                                                            {message.delivery_rate?.toFixed(1) || 0}%
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-500">Ouverture:</span>
                                                        <span className="font-medium text-blue-600">
                                                            {message.open_rate?.toFixed(1) || 0}%
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Actions de la carte */}
                                        <div className="flex justify-end space-x-2">
                                            <button
                                                onClick={() => openMessageModal(message)}
                                                className="flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                                                title="Voir les détails"
                                            >
                                                <Eye className="w-3 h-3 mr-1" />
                                                Voir
                                            </button>
                                            {!isReadOnly && (message.status === 'draft' || message.status === 'scheduled') && (
                                                <>
                                                    <button
                                                        onClick={() => openEditModal(message)}
                                                        className="flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                                                        title="Modifier"
                                                    >
                                                        <Edit className="w-3 h-3 mr-1" />
                                                        Modif.
                                                    </button>
                                                    <button
                                                        onClick={() => handleSendMessage(message.id, true)}
                                                        disabled={loadingActions[message.id]}
                                                        className="flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors disabled:opacity-50"
                                                        title="Envoyer maintenant"
                                                    >
                                                        <Send className="w-3 h-3 mr-1" />
                                                        Envoi
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMessage(message.id)}
                                                        disabled={loadingActions[message.id]}
                                                        className="flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-3 h-3 mr-1" />
                                                        Suppr.
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Vue desktop - Tableau */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Message</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Statut</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Destinataires</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Programmé</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Statistiques</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {messages.map((message) => (
                                        <tr key={message.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="cursor-pointer" onClick={() => openMessageModal(message)}>
                                                    <h3 className="font-semibold text-gray-900 hover:text-purple-600 transition-colors">
                                                        {message.subject}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                        {message.excerpt || message.generateExcerpt?.() || 'Aucun extrait disponible'}
                                                    </p>
                                                    <div className="flex items-center mt-2 text-xs text-gray-500">
                                                        <User className="w-3 h-3 mr-1" />
                                                        {message.sender?.first_name} {message.sender?.last_name}
                                                        <span className="mx-2">•</span>
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        {formatDate(message.created_at)}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    {message.type === 'email' && <Mail className="w-3 h-3 mr-1" />}
                                                    {message.type}
                                                </span>
                                            </td>

                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(message.status)}`}>
                                                    {getStatusIcon(message.status)}
                                                    <span className="ml-1">{getStatusLabel(message.status)}</span>
                                                </span>
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="font-medium text-gray-900">{message.recipients_count} destinataires</div>
                                                    <div className="text-gray-500">Type: {message.recipient_type}</div>
                                                </div>
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {message.scheduled_at ? formatDate(message.scheduled_at) : '-'}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4">
                                                {message.status === 'sent' && (
                                                    <div className="text-sm">
                                                        <div className="text-gray-900">
                                                            {message.delivery_rate?.toFixed(1) || 0}% livraison
                                                        </div>
                                                        <div className="text-gray-500">
                                                            {message.open_rate?.toFixed(1) || 0}% ouverture
                                                        </div>
                                                    </div>
                                                )}
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => openMessageModal(message)}
                                                        className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                                        title="Voir les détails"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>

                                                    {!isReadOnly && (message.status === 'draft' || message.status === 'scheduled') && (
                                                        <>
                                                            <button
                                                                onClick={() => openEditModal(message)}
                                                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Modifier"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>

                                                            <button
                                                                onClick={() => handleSendMessage(message.id, true)}
                                                                disabled={loadingActions[message.id]}
                                                                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                                                title="Envoyer maintenant"
                                                            >
                                                                <Send className="w-4 h-4" />
                                                            </button>

                                                            <button
                                                                onClick={() => handleDeleteMessage(message.id)}
                                                                disabled={loadingActions[message.id]}
                                                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                    <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                        <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                            Affichage de {((pagination.current_page - 1) * pagination.per_page) + 1} à {Math.min(pagination.current_page * pagination.per_page, pagination.total)} sur {pagination.total} résultats
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            <button
                                onClick={() => changePage(pagination.current_page - 1)}
                                disabled={pagination.current_page === 1}
                                className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            
                            {/* Pages numérotées */}
                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                const pageNumber = Math.max(1, pagination.current_page - 2) + i;
                                if (pageNumber > pagination.last_page) return null;
                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => changePage(pageNumber)}
                                        className={`px-2 sm:px-3 py-2 border rounded-lg transition-colors text-sm ${
                                            pagination.current_page === pageNumber
                                                ? 'bg-purple-600 text-white border-purple-600'
                                                : 'border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}

                            <button
                                onClick={() => changePage(pagination.current_page + 1)}
                                disabled={pagination.current_page === pagination.last_page}
                                className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de détails du message */}
            {showMessageModal && selectedMessage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-2xl font-bold text-gray-900">Détails du Message</h2>
                            <button
                                onClick={closeMessageModal}
                                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Informations générales */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Sujet</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedMessage.subject}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Type</label>
                                        <p className="mt-1 text-sm text-gray-900 capitalize">{selectedMessage.type}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Statut</label>
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedMessage.status)}`}>
                                            {getStatusIcon(selectedMessage.status)}
                                            <span className="ml-1">{getStatusLabel(selectedMessage.status)}</span>
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Expéditeur</label>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {selectedMessage.sender?.first_name} {selectedMessage.sender?.last_name}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Contenu du message */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contenu</h3>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(selectedMessage.body) }} />
                                </div>
                            </div>

                            {/* Statistiques d'envoi */}
                            {selectedMessage.status === 'sent' && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques d'envoi</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-blue-600">{selectedMessage.total_sent || 0}</div>
                                            <div className="text-sm text-blue-600">Envoyés</div>
                                        </div>
                                        <div className="bg-green-50 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-green-600">{selectedMessage.total_delivered || 0}</div>
                                            <div className="text-sm text-green-600">Livrés</div>
                                        </div>
                                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-purple-600">{selectedMessage.total_opened || 0}</div>
                                            <div className="text-sm text-purple-600">Ouverts</div>
                                        </div>
                                        <div className="bg-orange-50 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-orange-600">{selectedMessage.total_clicked || 0}</div>
                                            <div className="text-sm text-orange-600">Clics</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex justify-end space-x-3">
                                {!isReadOnly && (selectedMessage.status === 'draft' || selectedMessage.status === 'scheduled') && (
                                    <>
                                        <button
                                            onClick={() => {
                                                handleSendMessage(selectedMessage.id, true);
                                                closeMessageModal();
                                            }}
                                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            Envoyer maintenant
                                        </button>

                                        <button
                                            onClick={() => {
                                                handleDeleteMessage(selectedMessage.id);
                                                closeMessageModal();
                                            }}
                                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Supprimer
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de composition/édition */}
            <ComposeMessageModal
                isOpen={showComposeModal}
                onClose={closeEditModal}
                onMessageCreated={handleMessageCreated}
                editingMessage={editingMessage}
            />
        </div>
    );
};

export default AdminMailing;